<template>
  <div class="card-profile-actions">
    <div class="actions">
      <div class="left">
        <icon-button
          tooltip="Close card"
          icon="close"
          @click="close"
        />

        <icon-button
          v-if="page"
          tooltip="Minify back to panel"
          class="medium-up"
          icon="exit_to_app"
          @click="minify"
        />

        <icon-button
          v-else
          tooltip="View card in fullscreen mode"
          class="medium-up"
          icon="fullscreen"
          @click="fullscreen"
        />
      </div>

      <div class="right">
        <verified-icon
          v-if="card.cardId"
          v-tooltip="{ content: 'Verified Profile', delay: { show: 1500, hide: 100 }, placement: 'bottom' }"
        />

        <icon-button
          v-if="cardId"
          :tooltip="inCollection ? 'Remove from Collection' : 'Add to Collection'"
          :icon="inCollection ? 'delete_outline' : 'bookmark_add'"
          @click="handleCollection"
        />

        <icon-button
          v-else
          tooltip="Remove from Collection"
          icon="delete_outline"
          @click="removeFromCollection"
        />

        <dropdown
          align="right"
          :active="menuOpen"
          @close="menuOpen = false"
        >
          <div>
            <icon-button
              icon="more_vert"
              :loading="refreshingStats"
              @click="menuOpen = !menuOpen"
            />
          </div>

          <template #menu>
            <dropdown-menu
              :options="actionOptions"
              @select="handleSelect"
            />
          </template>
        </dropdown>
      </div>
    </div>

    <portal to="cardfeedback">
      <feedback-modal
        title="Tell us what's wrong"
        :open="showFeedbackModal"
        :card="card"
        @close="showFeedbackModal = false"
      />
    </portal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import * as Sentry from '@sentry/vue';
import firebase from 'firebase/compat/app';

import { authService, cardService } from '@/services';
import { constructWatchCard } from '@cardladder/shared/utils/watchlist.util';

import { DEFAULT_CARD_COLLECTION, DEFAULT_SALES_FILTERS, WATCHLIST_SIZE_LIMIT, COMPARE_LIMIT } from '@cardladder/shared/constants';
import { getFiltersUrlParams } from '@cardladder/shared/utils/ladder.util';
import VerifiedIcon from '@cardladder/shared/components/VerifiedIcon';

import FeedbackModal from '@/auth/FeedbackModal';

export default {
  components: {
    VerifiedIcon,
    FeedbackModal,
  },

  props: {
    cardId: String,
    card: Object,
    page: Boolean,
  },

  data() {
    return {
      refreshingStats: false,
      showFeedbackModal: false,
      menuOpen: false,
    };
  },

  mounted() {
    this.$bus.$on('keyboard-shortcut-e', this.handleKeyboardEdit);
    this.$bus.$on('keyboard-shortcut-p', this.handleKeyboardPop);
    this.$bus.$on('keyboard-shortcut-g', this.handleKeyboardGraph);
    this.$bus.$on('keyboard-shortcut-s', this.handleKeyboardStats);
    this.$bus.$on('keyboard-shortcut-d', this.handleKeybordCopyCard);
  },

  beforeDestroy() {
    this.$bus.$off('keyboard-shortcut-e', this.handleKeyboardEdit);
    this.$bus.$off('keyboard-shortcut-p', this.handleKeyboardPop);
    this.$bus.$off('keyboard-shortcut-g', this.handleKeyboardGraph);
    this.$bus.$off('keyboard-shortcut-s', this.handleKeyboardStats);
    this.$bus.$off('keyboard-shortcut-d', this.handleKeybordCopyCard);
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    ...mapGetters({
      isStaff: 'isStaff',
      isAdmin: 'isAdmin',
    }),

    actionOptions() {
      const options = [];

      if (this.isPrivateCollectionCard) {
        options.push({ value: 'edit', label: 'Edit Card' });
        options.push({ value: 'estimateValue', label: 'Estimate Value' });

        if (this.card.profileId && !this.cardId) {
          options.push({ value: 'viewSalesHistory', label: 'View Sales History' });
        }
      } else if (this.inCollection) {
        options.push({ value: 'edit', label: 'Edit Card' });
      }

      if (this.cardId) {
        if (this.card.profileId) {
          options.push({ value: 'viewPopReport', label: 'View Population Report' });
        }

        if (this.card.profileId && this.card.grader && this.card.grade) {
          options.push({ value: 'viewSalesHistory', label: 'View Sales History' });
        }

        if (this.showWatchlist) {
          options.push({ value: 'handleWatchlist', label: this.inWatchlist ? 'Remove from Watchlist' : 'Add to Watchlist' });
        }

        options.push({ value: 'compare', label: 'Compare this Card' });

        if (this.card.otherGrades?.length > 0) {
          options.push({ value: 'compareAllGrades', label: 'Compare All Grades' });
        }

        if (this.card.otherVariations?.length > 0) {
          options.push({ value: 'compareAllVariations', label: 'Compare All Variations' });
        }
      }

      if (this.showAdminAction) {
        options.push({ value: 'adminEdit', label: 'Edit Card', marker: 'Admin' });

        if (this.isAdmin && this.card.hidden) {
          options.push({ value: 'admitCard', label: 'Admit Card', marker: 'Admin' });
        }

        if (this.card.hidden && this.card.status) {
          options.push({ value: 'moveCard', label: `Move to ${this.card.status === 'review' ? 'Progress' : 'Review'}`, marker: 'Admin' });
        }

        options.push({ value: 'refreshStats', label: 'Refresh Stats', marker: 'Admin' });
        options.push({ value: 'adminCopyCard', label: 'Copy Card', marker: 'Admin' });
        options.push({ value: 'adminNeedsReview', label: `${this.card.needsReview ? 'Remove' : 'Add'} Needs Review`, marker: 'Admin' });
      }

      options.push({ value: 'copyCollectionCard', label: 'Copy Card' });

      if (this.cardId) {
        options.push({ value: 'handleFeedback', label: 'Missing or incorrect data?' });
      }

      return options;
    },

    isPrivateCollectionCard() {
      return this.card?.collectionCardId && !this.cardId;
    },


    parentRoute() {
      return this.$route.query.profile || 'ladder';
    },

    inCollection() {
      return Boolean(this.card?.collectionCardId);
    },

    inWatchlist() {
      return Boolean(this.card?.watchlistCardId);
    },

    showAdminAction() {
      return Boolean(this.isStaff && this.cardId && this.parentRoute === 'ladder');
    },

    showWatchlist() {
      return this.cardId && (this.inWatchlist || this.user.numCardsWatching < WATCHLIST_SIZE_LIMIT);
    },
  },

  methods: {
    handleSelect(value) {
      this.menuOpen = false;

      if (typeof this[value] === 'function') {
        this[value]();
      }
    },

    async refreshStats() {
      if (window.confirm('Are you sure you want to refresh the card stats?')) {
        this.refreshingStats = true;

        try {
          await firebase.firestore().collection('normalizeSales').doc(this.cardId).set({ start: true });
          await firebase.functions().httpsCallable('httpSyncCardStats')({ card: this.cardId, uid: this.user.uid });

          this.$toasted.success('Card stats have been calculated!');
        } catch (e) {
          this.$toasted.error('Failed to calculate card stats');
          Sentry.captureException(e);
        }

        setTimeout(() => {
          this.refreshingStats = false;
          this.$bus.$emit('card-profile-changed');
          this.$bus.$emit('add-cards-changed');
        }, 4000);
      }
    },

    adminCopyCard() {
      this.$bus.$emit('admin-copy-card', this.card);
    },

    copyCollectionCard() {
      this.$bus.$emit('collection-add-card', {
        player: this.card.player || '',
        year: this.card.year || '',
        set: this.card.set || '',
        variation: this.card.variation || '',
        condition: this.card.condition || '',
        number: this.card.number || '',
        category: this.card.category || '',
        investment: this.card.investment || 100,
        datePurchased: this.card.datePurchased || new Date(),
        quantity: this.card.quantity || 1,
      });
    },

    compare() {
      if (this.$route.path.includes('compare')) {
        this.$bus.$emit('add-compare-card', this.cardId);
      } else {
        this.$router.push(`/compare?ids=${this.cardId}`);
      }
    },

    compareAllGrades() {
      const ids = [
        this.cardId,
        ...this.card.otherGrades.map(({ id }) => id),
      ].slice(0, COMPARE_LIMIT).join(',');

      this.$router.push(`/compare?ids=${ids}`);
    },

    compareAllVariations() {
      const ids = [
        this.cardId,
        ...this.card.otherVariations.map(({ id }) => id),
      ].slice(0, COMPARE_LIMIT).join(',');

      this.$router.push(`/compare?ids=${ids}`);
    },

    handleCollection() {
      if (this.inCollection) {
        this.removeFromCollection();
      } else {
        this.addToCollection();
      }
    },

    async removeFromCollection() {
      if (window.confirm('Are you sure you want to remove this from your collection? All information about this card will be deleted.')) {
        try {
          await authService.removeCardFromCollection(this.user.uid, this.card.collectionId, this.card.collectionCardId);
          this.$bus.$emit('card-profile-changed');
          this.$bus.$emit('collection-changed', { remove: this.card.collectionCardId });
          this.$toasted.success('Removed from your collection!');
        } catch (e) {
          this.$toasted.error('Error removing from collection');
          Sentry.captureException(e);
        }
      }
    },

    addToCollection() {
      const card = {
        cardId: this.card.id,
        label: this.card.label,
        image: this.card.image,
        player: this.card.player,
        playerIndexId: this.card.playerIndexId || null,
        set: this.card.set || '',
        variation: this.card.variation || '',
        year: this.card.year || '',
        number: this.card.number || '',
        category: this.card.category,
        condition: this.card.condition,
        ...DEFAULT_CARD_COLLECTION,
        currentValue: this.card.stats?.currentValue || null,
        marketValue: this.card.stats?.marketValue || null,
        guideValue: this.card.stats?.guideValue || null,
      };

      this.$bus.$emit('collection-add-card', card);
    },

    handleWatchlist() {
      if (this.inWatchlist) {
        this.removeFromWatchlist();
      } else {
        this.addToWatchlist();
      }
    },

    async addToWatchlist() {
      try {
        const watchCard = constructWatchCard(this.user.uid, this.card);

        await authService.addToWatchlist(this.user.uid, watchCard);
        this.$toasted.success('Added to your watchlist!');
        this.$bus.$emit('card-profile-changed');
      } catch (e) {
        this.$toasted.error('Error adding to watchlist');
        Sentry.captureException(e);
      }
    },

    async removeFromWatchlist() {
      if (window.confirm('Are you sure you want to remove this card from your watchlist?')) {
        try {
          await authService.removeFromWatchlist(this.user.uid, this.card.watchlistCardId);
          this.$toasted.success('Removed from your watchlist!');
          this.$bus.$emit('card-profile-changed');
          this.$bus.$emit('watchlist-changed', { remove: this.card.watchlistCardId });
        } catch (e) {
          this.$toasted.error('Error removing from watchlist');
          Sentry.captureException(e);
        }
      }
    },

    async admitCard() {
      if (window.confirm('Are you sure you want to admit this card to the database?')) {
        this.refreshingStats = true;

        try {
          await cardService.admitCardsInReview([this.card.id]);
        } catch (e) {
          this.$toasted.error('Error admitting card to the database');
          Sentry.captureException(e);
        }

        setTimeout(() => {
          this.refreshingStats = false;
          this.$bus.$emit('card-profile-changed');
          this.$bus.$emit('add-cards-changed');
          this.$toasted.success('Card has been admitted to the database!');
        }, 4000);
      }
    },

    async moveCard() {
      if (window.confirm('Are you sure you want to move this card?')) {
        this.refreshingStats = true;

        try {
          await cardService.updateCardStatuses([this.card.id], this.card.status === 'review' ? 'progress' : 'review');
        } catch (e) {
          this.$toasted.error('Error moving card');
          Sentry.captureException(e);
        }

        setTimeout(() => {
          this.refreshingStats = false;
          this.$bus.$emit('card-profile-changed');
          this.$bus.$emit('add-cards-changed');
          this.$toasted.success('Card has been moved!');
        }, 4000);
      }
    },

    async adminNeedsReview() {
      try {
        await cardService.updateNeedsReview(this.card.id, !this.card.needsReview);
        this.$bus.$emit('card-profile-changed');
      } catch (e) {
        this.$toasted.error('Error admin needs review');
        Sentry.captureException(e);
      }
    },

    edit() {
      this.$bus.$emit('collection-edit-card', {
        id: this.card.collectionCardId,
        collectionId: this.card.collectionId,
        display: 'edit',
      });
    },

    estimateValue() {
      this.$bus.$emit('collection-edit-card-value', {
        id: this.card.collectionCardId,
        collectionId: this.card.collectionId,
        display: 'valueIntro',
      });
    },

    viewSalesHistory() {
      const filters = {
        ...DEFAULT_SALES_FILTERS,
        profileId: this.card.profileId,
      };

      if (this.card.grader) {
        filters.grader = this.card.grader;
      }

      if (this.card.grade) {
        filters.grade = this.card.grade;
      }

      this.$router.push({
        path: '/sales-history',
        query: {
          sort: 'date',
          direction: 'desc',
          filters: getFiltersUrlParams(filters, true),
        },
      });
    },

    handleKeyboardEdit() {
      if (this.inCollection) {
        this.edit();
      } else if (this.showAdminAction) {
        this.adminEdit();
      }
    },

    handleKeyboardGraph() {
      if (this.showAdminAction) {
        this.refreshStats();
      }
    },

    handleKeyboardStats() {
      if (this.showAdminAction) {
        this.refreshStats();
      }
    },

    handleKeybordCopyCard() {
      if (this.showAdminAction) {
        this.adminCopyCard();
      }
    },

    adminEdit() {
      this.$bus.$emit('admin-edit-card', this.cardId);
    },

    close() {
      if (this.page) {
        const query = { ...this.$route.query };
        delete query.backTo;
        delete query.backToName;
        delete query.showSales;

        const to = {
          name: this.$route.query.backTo || 'Ladder',
          query,
        };

        if (this.$route.query.backToName) {
          to.params = { name: this.$route.query.backToName };
        }

        this.$router.push(to);
      } else {
        const query = { ...this.$route.query };
        delete query.cardId;
        delete query.showSales;
        this.$router.push({ query });
      }
    },

    fullscreen() {
      const query = {
        ...this.$route.query,
        backTo: this.$route.name,
      };
      delete query.cardId;

      if (this.$route.name === 'Player' || this.$route.name === 'Index') {
        query.backToName = this.$route.params.name;
      }

      this.$router.push({
        path: `/card/${this.$route.query.cardId}`,
        query,
      });
    },

    minify() {
      const query = {
        ...this.$route.query,
        cardId: this.$route.params.id,
      };
      delete query.backTo;
      delete query.backToName;
      delete query.showSales;

      const to = {
        name: this.$route.query.backTo || 'Ladder',
        query,
      };

      if (this.$route.query.backToName) {
        to.params = { name: this.$route.query.backToName };
      }

      this.$router.push(to);
    },

    viewPopReport() {
      this.$router.push(`/profiles/${this.card.profileId}`);
    },

    handleFeedback() {
      this.showFeedbackModal = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.actions {
  margin-bottom: $gp;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--theme-background);
}

.left,
.right {
  > *:not(:first-child) {
    line-height: 0;
    margin-left: $gp * .5;
  }
}
</style>
